import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Grid, TablePagination, Link
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon } from '@mui/icons-material';
import { listWholesaleOrders, listWholesaleCustomers } from '../../services/retailOrdersService';

const STATUS_COLOR = {
  pending: 'warning',
  shipped: 'info',
  fulfilled: 'info',
  delivered: 'success',
  cancelled: 'error',
};

const PAYMENT_COLOR = {
  pending: 'default',
  invoiced: 'info',
  paid: 'success',
  overdue: 'error',
};

export default function CustomerHistory() {
  const { qbCustomerId } = useParams();
  const [orders, setOrders] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [o, c] = await Promise.all([
          listWholesaleOrders({ customer_id: qbCustomerId }),
          listWholesaleCustomers(),
        ]);
        setOrders(o.data || []);
        const all = c.data || [];
        const match = all.find(x => x.qb_customer_id === qbCustomerId);
        setCustomer(match || null);
      } catch (e) {
        setError(e.response?.data?.error || e.message);
      } finally { setLoading(false); }
    })();
  }, [qbCustomerId]);

  const stats = useMemo(() => {
    let total = 0, lifetime_value = 0, pending = 0, shipped = 0, invoiced = 0;
    const byCurrency = {};
    for (const o of orders) {
      total++;
      // Lifetime value should reflect what the customer actually paid =
      // grand total (revenue + tax + shipping + surcharge − discount), not
      // just the pre-tax line revenue.
      const v = Number(o.grand_total ?? o.order_revenue) || 0;
      lifetime_value += v;
      // DT-1453: an order with no currency is its own plain-number bucket, never added to USD.
      const ccy = o.currency || '';
      byCurrency[ccy] = (byCurrency[ccy] || 0) + v;
      if (o.fulfillment_status === 'pending') pending++;
      if (o.fulfillment_status === 'shipped') shipped++;
      if (o.qb_invoice_id) invoiced++;
    }
    return { total, lifetime_value, pending, shipped, invoiced, byCurrency };
  }, [orders]);

  const paged = orders.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box mb={2}>
        <Button startIcon={<ArrowBackIcon />} component={RouterLink} to="/retail/customers">
          All Customers
        </Button>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {customer?.customer_name || customer?.display_name || qbCustomerId}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Order history — every wholesale order placed by this customer
          </Typography>
        </Box>
        {customer?.cwe_id && (
          <Button startIcon={<EditIcon />} variant="outlined"
            component={RouterLink} to={`/retail/customers?edit=${customer.cwe_id}`}>
            Edit Customer
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {customer && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Tier</Typography>
                <Typography>{customer.tier_name || '—'}{customer.currency ? ` (${customer.currency})` : (customer.tier_name ? ' (currency not set)' : '')}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Payment Terms</Typography>
                <Typography>{customer.payment_terms || '—'}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Send Via</Typography>
                <Chip size="small" label={customer.send_method || 'qb'} />
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">CIN7 ID</Typography>
                <Typography sx={{ fontFamily: 'monospace' }}>{(customer.qb_customer_id || '').replace(/^CIN7-/, '')}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Delivery Address</Typography>
                <Typography>
                  {[customer.delivery_address_1, customer.delivery_city, customer.delivery_country].filter(Boolean).join(', ') || '—'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Contact</Typography>
                <Typography>{[customer.contact_first_name, customer.contact_last_name].filter(Boolean).join(' ') || '—'}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Email</Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{customer.contact_email || '—'}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Card><CardContent>
            <Typography variant="caption" color="textSecondary">Total orders</Typography>
            <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent>
            <Typography variant="caption" color="textSecondary">Pending ship</Typography>
            <Typography variant="h4" fontWeight="bold" color="warning.main">{stats.pending}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent>
            <Typography variant="caption" color="textSecondary">Shipped not invoiced</Typography>
            <Typography variant="h4" fontWeight="bold" color="info.main">{stats.shipped - (stats.invoiced - (stats.total - stats.pending - stats.shipped))}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent>
            <Typography variant="caption" color="textSecondary">Lifetime value</Typography>
            <Typography variant="body2" fontWeight="bold">
              {Object.entries(stats.byCurrency).map(([c, v]) => `${c} ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`.trim()).join(' · ') || '—'}
            </Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>{orders.length} order{orders.length === 1 ? '' : 's'}</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order #</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Shipped</TableCell>
                  <TableCell>From WH</TableCell>
                  <TableCell>PO #</TableCell>
                  <TableCell>Fulfillment</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>QB Invoice</TableCell>
                  <TableCell align="right">Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length === 0 ? (
                  <TableRow><TableCell colSpan={10} align="center">
                    <Typography color="textSecondary" sx={{ py: 2 }}>No orders for this customer yet.</Typography>
                  </TableCell></TableRow>
                ) : paged.map(o => (
                  <TableRow key={`${o.brand_id}-${o.order_id}`} hover>
                    <TableCell>
                      <Box component={RouterLink}
                        to={`/wholesale/new?edit=${encodeURIComponent(o.order_id)}&brand=${o.brand_id}`}
                        sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        {(o.order_id || '').replace(/^CIN7-/, '')}
                      </Box>
                    </TableCell>
                    <TableCell><Chip size="small" label={o.brand_name} /></TableCell>
                    <TableCell>{o.event_date}</TableCell>
                    <TableCell>{o.shipped_date || '—'}</TableCell>
                    <TableCell>{o.warehouse_code || '—'}</TableCell>
                    <TableCell>{o.po_number || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={o.fulfillment_status || '—'}
                        color={STATUS_COLOR[o.fulfillment_status] || 'default'} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={o.payment_status || '—'}
                        color={PAYMENT_COLOR[o.payment_status] || 'default'} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{o.qb_invoice_id || '—'}</TableCell>
                    <TableCell align="right">{o.currency} {Number(o.grand_total ?? o.order_revenue).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={orders.length} page={page}
            onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]} />
        </CardContent>
      </Card>
    </Box>
  );
}
